import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RUN_HEIGHT, RUN_WIDTH, clipperRunScore, type Phase } from "@/lib/loading-game/clipperRun";
import {
  createRunSimulation, tickRunSimulation, MAX_RUN_FRAMES, RUN_FPS,
  type RunInputEvent, type RunAction,
} from "@/lib/loading-game/clipperRunReplay";
import { createRunScoreRecorder, type OwnedRunAdmission, type RunSaveStatus } from "@/lib/loading-game/runScoreRecorder";
import { runScoreIdentity, runScoreTransport } from "@/lib/loading-game/runScoreTransport";
import { drawClipperRun, type ClipperRunPalette } from "@/components/loading-game/drawClipperRun";
import { loadClipperSprites } from "./clipperSprites";

const BEST_KEY = "autoclipper:clipper-run:best";

function readBest(): number {
  try {
    const raw = window.localStorage.getItem(BEST_KEY);
    if (!raw) return 0;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  } catch {
    return 0;
  }
}

function writeBest(score: number): void {
  try {
    window.localStorage.setItem(BEST_KEY, String(score));
  } catch {
    // A storage failure still shows the record for this visit.
  }
}

function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function hslVar(name: string, fallback: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!raw) return fallback;
  if (raw.startsWith("#") || raw.startsWith("rgb") || raw.startsWith("hsl")) return raw;
  return `hsl(${raw})`;
}

function readPalette(): ClipperRunPalette {
  return {
    background: hslVar("--muted", "hsl(220 15% 12%)"),
    foreground: hslVar("--foreground", "hsl(210 40% 98%)"),
    mutedForeground: hslVar("--muted-foreground", "hsl(215 20% 65%)"),
    primary: hslVar("--primary", "hsl(75 100% 50%)"),
  };
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest("input, textarea, select, button, a")) return true;
  return target instanceof HTMLElement && target.isContentEditable;
}

function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') != null;
}

function prepareContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.round(RUN_WIDTH * dpr);
  const height = Math.round(RUN_HEIGHT * dpr);
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  return context;
}

// Leaves every recording below the browser's shared 64 KiB keepalive budget.
const CLIENT_EVENT_LIMIT = 1200;
const randomSeed = () => Math.floor(Math.random() * 2147483646) + 1;

export const ClipperRunGame = () => {
  const { t } = useTranslation();
  const translate = useRef(t);
  translate.current = t;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const jumpQueued = useRef(false);
  const duckHeld = useRef(false);
  const bestRef = useRef(0);
  const scoreRef = useRef<HTMLParagraphElement>(null);
  const [phase, setPhase] = useState<Phase>("ready");
  const [best, setBest] = useState(0);
  const [saveStatus, setSaveStatus] = useState<RunSaveStatus | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    let simulation = createRunSimulation(randomSeed());
    let admission: OwnedRunAdmission | null = null;
    let events: RunInputEvent[] = [];
    let sprites: HTMLImageElement | null = null;
    let frame = 0;
    let last = performance.now();
    let accumulator = 0;
    let stopped = false;
    const updateBest = (score: number) => {
      if (score > bestRef.current) {
        bestRef.current = score;
        writeBest(score);
        if (!stopped) setBest(score);
      }
    };
    bestRef.current = readBest();
    setBest(bestRef.current);
    const recorder = createRunScoreRecorder({
      transport: runScoreTransport, identity: runScoreIdentity,
      onBest: updateBest,
      onStatus: (status) => { if (!stopped) setSaveStatus(status); },
    });
    void recorder.prepare();
    void loadClipperSprites().then((image) => { if (!stopped) sprites = image; });

    const flush = (keepalive = false) => {
      const finished = admission;
      admission = null;
      void recorder.complete(finished, { frames: simulation.frame, events }, keepalive);
    };
    const paint = (now: number) => {
      const context = prepareContext(canvas);
      if (!context) return;
      drawClipperRun(context, simulation.state, {
        now, best: bestRef.current, sprites,
        reducedMotion: prefersReducedMotion(), palette: readPalette(),
        labels: { best: translate.current("clipperRun.best"), gameOver: translate.current("clipperRun.gameOver") },
      });
    };
    const step = () => {
      if (jumpQueued.current && simulation.state.phase !== 'playing') {
        admission = recorder.take();
        simulation = createRunSimulation(admission?.seed ?? randomSeed());
        events = [];
        setSaveStatus(admission || !runScoreIdentity() ? null : 'local');
      }
      if (simulation.state.phase === 'dead' || (simulation.state.phase === 'ready' && !jumpQueued.current)) return;
      const actions: RunAction[] = [];
      if (jumpQueued.current) { actions.push('jump'); jumpQueued.current = false; }
      if (duckHeld.current !== simulation.duckHeld) actions.push(duckHeld.current ? 'duck' : 'stand');
      if (admission && (simulation.frame >= MAX_RUN_FRAMES || events.length + actions.length > CLIENT_EVENT_LIMIT)) flush();
      if (admission) events.push(...actions.map((action) => ({ frame: simulation.frame, action })));
      const previous = simulation.state.phase;
      tickRunSimulation(simulation, actions);
      const nextPhase = simulation.state.phase as Phase;
      if (nextPhase !== previous) setPhase(nextPhase);
      if (nextPhase === 'dead') {
        updateBest(clipperRunScore(simulation.state));
        flush();
        void recorder.prepare();
      }
    };
    const tick = (now: number) => {
      if (stopped) return;
      if (document.hidden) {
        accumulator = 0;
        duckHeld.current = false;
        jumpQueued.current = false;
      } else {
        accumulator += Math.min(Math.max((now - last) / 1000, 0), 0.1);
        while (accumulator + 1e-9 >= 1 / RUN_FPS) {
          step();
          accumulator = Math.max(0, accumulator - 1 / RUN_FPS);
        }
        const score = clipperRunScore(simulation.state);
        root.dataset.phase = simulation.state.phase;
        root.dataset.score = String(score);
        if (scoreRef.current) scoreRef.current.textContent = `${translate.current("clipperRun.score")} ${score}`;
        paint(now);
      }
      last = now;
      frame = window.requestAnimationFrame(tick);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat && !['ArrowDown', 's', 'S'].includes(event.key)) return;
      if (isTypingTarget(event.target) || dialogOpen()) return;
      if (event.key === " " || event.key === "ArrowUp") {
        event.preventDefault(); jumpQueued.current = true;
      } else if (['ArrowDown', 's', 'S'].includes(event.key)) {
        event.preventDefault(); duckHeld.current = true;
      }
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (['ArrowDown', 's', 'S'].includes(event.key)) duckHeld.current = false;
    };
    const onBlur = () => { duckHeld.current = false; jumpQueued.current = false; };
    const onVisibility = () => { onBlur(); accumulator = 0; last = performance.now(); };
    const onPageHide = () => { flush(true); };
    root.dataset.phase = simulation.state.phase;
    root.dataset.score = '0';
    paint(last);
    frame = window.requestAnimationFrame(tick);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      flush(true);
      recorder.dispose();
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return (
    <div ref={rootRef} className="w-full select-none" data-testid="clipper-run" data-best={best}>
      <canvas
        ref={canvasRef} role="img" aria-label={t("clipperRun.label")} aria-describedby="clipper-run-hint"
        width={RUN_WIDTH} height={RUN_HEIGHT}
        className="block h-auto w-full touch-none rounded-md border border-border bg-muted"
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault(); jumpQueued.current = true;
        }}
      />
      <p ref={scoreRef} className="sr-only">{t("clipperRun.score")} 0</p>
      <p id="clipper-run-hint" className="mt-2 text-center text-xs text-muted-foreground" aria-live="polite">
        {phase === 'dead' ? t("clipperRun.retry") : t("clipperRun.hint")}
      </p>
      {saveStatus && <p className={saveStatus === 'local' ? "mt-1 text-center text-xs text-muted-foreground" : "sr-only"} role="status">{t(`clipperRun.${saveStatus}`)}</p>}
    </div>
  );
};
